import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { T, Var, useGT } from 'gt-react';

import Page from 'src/components/page/Page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useLabeledRecord,
  useUndoLabel,
} from '@/api/rpc/hooks/ship-and-debit/useLabeling';
import type { LabelingQueueKind } from '@erp/rpc';

const PAGE_SIZE = 25;

/**
 * The labeled record — the "another page" half of the queue.
 *
 * An item disappears from the queue the moment it's labeled, so it has to stay
 * visible somewhere: what was decided, by whom, when, and whether it agreed with the
 * engine. "Return to queue" is the durable form of Undo, for a mistake noticed after
 * the toast is gone.
 */
export function LabeledRecordPage() {
  const gt = useGT();
  const [kind, setKind] = useState<LabelingQueueKind>('emails');
  const [page, setPage] = useState(1);
  const { data, isLoading } = useLabeledRecord({
    kind,
    page,
    pageSize: PAGE_SIZE,
  });
  const undo = useUndoLabel();

  return (
    <Page
      title={gt('Labeled', { context: 'labeled page title' })}
      subtitle={gt('Every 849 label an expert has recorded, newest first.', {
        context: 'labeled page subtitle',
      })}
      actions={
        <Link to="/ship-and-debit/labeling">
          <Button variant="outline">
            <T context="labeled page action">Back to queue</T>
          </Button>
        </Link>
      }
    >
      <div className="space-y-4">
        <Tabs
          value={kind}
          onValueChange={(v) => {
            setKind(v as LabelingQueueKind);
            setPage(1);
          }}
        >
          <TabsList>
            <TabsTrigger value="emails">
              <T context="labeled page tab">Emails</T>
            </TabsTrigger>
            <TabsTrigger value="matches">
              <T context="labeled page tab">Matches</T>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : !data?.items.length ? (
          <Card className="p-10 text-center text-muted-foreground">
            <T context="labeled page empty state">Nothing labeled yet.</T>
          </Card>
        ) : (
          <Card className="divide-y">
            {data.items.map((row) => (
              <div
                key={row.labelId}
                className="flex items-center justify-between gap-4 px-4 py-3"
              >
                <div className="min-w-0 space-y-1">
                  <div className="truncate text-sm font-medium">
                    {row.label}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    <Badge variant="outline" className="font-normal">
                      {row.value}
                    </Badge>
                    {row.agreedWithEngine === true ? (
                      <Badge variant="secondary" className="font-normal">
                        <T context="labeled page badge">agreed with engine</T>
                      </Badge>
                    ) : row.agreedWithEngine === false ? (
                      <Badge variant="destructive" className="font-normal">
                        <T context="labeled page badge">corrected the engine</T>
                      </Badge>
                    ) : null}
                    {row.relabelled ? (
                      <Badge variant="outline" className="font-normal">
                        <T context="labeled page badge">re-labeled</T>
                      </Badge>
                    ) : null}
                    <span>{new Date(row.labeledAt).toLocaleString()}</span>
                    {/* Attribution, not decoration: the label store keeps ONE live
                        row per labeler per subject, so "who decided this" is the
                        question this page is here to answer.

                        Three states, not two. `labeledByName` is null for TWO
                        different reasons — a system/agent label (no user at all) and
                        a human whose user row no longer resolves — so branching on
                        the NAME would report a person's judgement as automated. The
                        id is what distinguishes them, so branch on that first. */}
                    <span>
                      {row.labeledByUserId == null ? (
                        <T context="labeled page labeler">by automation</T>
                      ) : row.labeledByName ? (
                        <T context="labeled page labeler">
                          by <Var>{row.labeledByName}</Var>
                        </T>
                      ) : (
                        <T context="labeled page labeler">by a former user</T>
                      )}
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={undo.isPending}
                  onClick={() =>
                    undo.mutate({ kind, subjectId: row.subjectId })
                  }
                >
                  <T context="labeled page row action">Return to queue</T>
                </Button>
              </div>
            ))}
          </Card>
        )}

        {data && data.totalPages > 1 ? (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {data.totalCount} <T context="labeled page total count">labels</T>
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <T context="labeled page pagination">Previous</T>
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= data.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                <T context="labeled page pagination">Next</T>
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Page>
  );
}
